/* ---
	Scheme fade — whole-background scroll morph (FBシート No.8)

	brand.b-ownd.com fades its page background from green to black while
	you scroll; this is the white→black version. The backgrounds painted
	by each top-level section are lifted off (set transparent) and the
	main content paints ONE background colour instead, interpolated per
	scroll frame:

	- each section contributes a target colour (detected from its
	  computed background, so bespoke sections like home-order-works
	  work the same as wrapper-based ones);
	- while a boundary between two differently-coloured sections travels
	  up the viewport, the whole background — not a printed band —
	  crossfades between the two colours;
	- the footer takes part in the morph too: its black paint is lifted
	  off and the ONE interpolated colour lives on <body>, behind main
	  and footer alike. With a single shared background a seam between
	  content and footer cannot exist — the footer surfaces in the
	  current page colour and deepens to #000 as it comes fully in;
	- every fade window is clamped so it is 0 at scroll position 0: a
	  short page loads in its true colours and only morphs once the
	  user actually scrolls.
--- */

(() => {
  const main = document.querySelector('.kt-main-content');
  if (!main) return;

  const footer = document.querySelector('.kt-main-footer');
  const LIGHT = { r: 255, g: 255, b: 255 };
  const BLACK = { r: 0, g: 0, b: 0 };

  let stops = [];
  let cleared = [];

  const parseColor = (str) => {
    const match = /rgba?\(([^)]+)\)/.exec(str || '');
    if (!match) return null;
    const parts = match[1].split(',').map(parseFloat);
    return { r: parts[0], g: parts[1], b: parts[2], a: parts.length > 3 ? parts[3] : 1 };
  };

  const mix = (a, b, t) => ({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  });

  const clamp01 = (v) => Math.min(1, Math.max(0, v));

  const smoothstep = (t) => t * t * (3 - 2 * t);

  const luminance = (c) => (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;

  /* The wrapper itself is usually transparent — the paint lives on the
     first schemed element inside it. */
  const backgroundHolder = (wrapper) => {
    const candidates = [wrapper];
    const schemed = wrapper.querySelector('[class*="kt-color-scheme-"]');
    if (schemed) candidates.push(schemed);
    const section = wrapper.querySelector('.kt-section');
    if (section) candidates.push(section);
    for (const el of candidates) {
      const color = parseColor(getComputedStyle(el).backgroundColor);
      if (color && color.a > 0.5) return { el, color };
    }
    return null;
  };

  const restore = () => {
    cleared.forEach(({ el, prev }) => {
      el.style.backgroundColor = prev;
    });
    cleared = [];
  };

  const lift = (el) => {
    cleared.push({ el, prev: el.style.backgroundColor });
    el.style.backgroundColor = 'transparent';
  };

  const build = () => {
    restore();

    /* .kt-main-container paints the page scheme background and would
       hide the body colour — its paint moves to the body too. */
    const container = document.querySelector('.kt-main-container');
    if (container) lift(container);

    const sections = Array.from(main.children).filter(
      (el) => el.classList.contains('shopify-section') && el.offsetHeight > 0,
    );

    stops = sections.map((wrapper) => {
      const found = backgroundHolder(wrapper);
      if (found) {
        lift(found.el);
        return { wrapper, color: found.color };
      }
      return { wrapper, color: LIGHT };
    });

    /* The footer joins the morph: its own black paint comes off so the
       body colour shows through it, and it darkens with the scroll
       instead of butting a solid #000 edge against the page. */
    if (footer) {
      lift(footer);
      footer.querySelectorAll('.shopify-section').forEach((wrapper) => {
        const found = backgroundHolder(wrapper);
        if (found) lift(found.el);
      });
    }

    update();
  };

  const update = () => {
    const vh = window.innerHeight;
    const y = window.scrollY;

    /* The crossfade runs while the boundary travels from 78% to 28% of
       the viewport height — a band around the centre. Completing at 28%
       (not at the very top) matters: a section shorter than the screen
       must reach 100% of its colour (true #000) before its far edge
       starts fading back, otherwise the darkest point tops out at grey.
       The window start is clamped to the boundary's position at scroll 0,
       so a boundary that loads inside the window still starts at t=0 —
       short pages keep their true colours until the user scrolls. */
    const boundaryProgress = (docTop, endOverride) => {
      const startP = Math.min(0.78 * vh, docTop);
      const endP = endOverride === undefined ? 0.28 * vh : endOverride;
      if (startP <= endP) return 1;
      const p = docTop - y;
      return smoothstep(clamp01((startP - p) / (startP - endP)));
    };

    /* Pages Shopify renders without .shopify-section wrappers (the
       /policies/ pages) have no stops at all — they are plain light
       pages, and the footer morph below must still run for them. */
    let color = stops.length ? stops[0].color : LIGHT;
    for (let i = 1; i < stops.length; i++) {
      const rect = stops[i].wrapper.getBoundingClientRect();
      const t = boundaryProgress(rect.top + y);
      if (t <= 0) break;
      color = mix(color, stops[i].color, t);
    }

    /* Footer: since it shares the body background it is just one more
       boundary whose target is black. For the fixed reveal footer the
       "boundary" is the bottom edge of the main content — the line the
       footer surfaces under. */
    if (footer && footer.offsetHeight > 0) {
      const fixedReveal =
        document.body.classList.contains('kt-main-footer--is-fixed') &&
        !document.body.classList.contains('kt-main-footer--is-taller');
      const footerBoundary = fixedReveal
        ? document.documentElement.scrollHeight - footer.offsetHeight
        : footer.getBoundingClientRect().top + y;
      /* A footer shorter than ~72% of the viewport never carries its
         boundary down to the default end position — finish the fade at
         wherever the boundary sits at maximum scroll instead. */
      const footerEnd = Math.max(0.28 * vh, vh - footer.offsetHeight);
      color = mix(color, BLACK, boundaryProgress(footerBoundary, footerEnd));
    }

    document.body.style.backgroundColor = `rgb(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(color.b)})`;

    /* How far dark-text content should shift towards white to stay
       readable on the current background (0 = light bg, 1 = black bg).
       Consumed via color-mix, e.g. by the breadcrumbs. */
    const invert = smoothstep(clamp01((0.62 - luminance(color)) / 0.3));
    document.body.style.setProperty('--kt-scheme-fade-invert', invert.toFixed(3));
  };

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      update();
      ticking = false;
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  /* Lazy media changes section heights well after load. */
  window.addEventListener('load', build, { once: true });

  const editorRebuild = () => window.setTimeout(build, 50);
  document.addEventListener('shopify:section:load', editorRebuild);
  document.addEventListener('shopify:section:unload', editorRebuild);
  document.addEventListener('shopify:section:reorder', editorRebuild);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }
})();
